/* Prices the configurator and mirrors the result into hidden fields so the
   emailed quote is self-contained — the shop should not have to reconstruct the
   arithmetic from the product and quantity alone. */

class SpikeyQuote extends HTMLElement {
  connectedCallback() {
    this.currency = this.dataset.currency || '$';

    this.productInputs = Array.from(this.querySelectorAll('[data-pricing]'));
    this.qtyInputs = Array.from(this.querySelectorAll('[data-qty], [data-custom]'));
    this.customWrap = this.querySelector('[data-quote-custom-wrap]');
    this.customInput = this.querySelector('[data-quote-custom-input]');

    this.fields = {
      qty: this.querySelector('[data-quote-qty-field]'),
      unit: this.querySelector('[data-quote-field-unit]'),
      total: this.querySelector('[data-quote-field-total]'),
    };

    // Preselect the lowest tier so a submitted quote always carries a quantity.
    // Done here rather than in Liquid because the tier list is editable and may
    // be reordered, and the smallest is the honest default for a first visit.
    if (!this.qtyInputs.some((i) => i.checked)) {
      const first = this.qtyInputs.find((i) => i.dataset.qty !== undefined);
      if (first) first.checked = true;
    }

    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onChange);
    this.#update();
  }

  #onChange = () => this.#update();

  #num(value, fallback) {
    const n = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(n) ? n : fallback;
  }

  /* "100: 5.95" per line into [{qty, price}], ascending. Tolerant of blank
     lines, stray currency symbols and thousands separators, because this is a
     free-text merchant field, not validated input. */
  #tiers(raw) {
    return String(raw || '')
      .split(/[\n;]+/)
      .map((line) => {
        const [q, p] = line.split(':');
        if (p === undefined) return null;
        const qty = this.#num(q.replace(/,/g, ''), NaN);
        const price = this.#num(p, NaN);
        return Number.isFinite(qty) && Number.isFinite(price) ? { qty, price } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.qty - b.qty);
  }

  #money(value) {
    return `${this.currency}${value.toFixed(2)}`;
  }

  #quantity() {
    const chosen = this.qtyInputs.find((i) => i.checked);
    if (!chosen) return 0;
    if (chosen.dataset.custom !== undefined) {
      return Math.max(0, Math.floor(this.#num(this.customInput?.value, 0)));
    }
    return this.#num(chosen.dataset.qty, 0);
  }

  #update() {
    const isCustom = this.qtyInputs.some((i) => i.checked && i.dataset.custom !== undefined);
    if (this.customWrap) this.customWrap.hidden = !isCustom;

    const product = this.productInputs.find((i) => i.checked) || this.productInputs[0];
    const tiers = this.#tiers(product?.dataset.pricing);
    const qty = this.#quantity();

    // Highest tier at or below the quantity — a volume break applies once you
    // reach it, so rounding up would undercharge. Below the smallest tier there
    // is no break to apply, so the smallest tier's price stands.
    let unit = null;
    for (const tier of tiers) {
      if (qty >= tier.qty) unit = tier.price;
    }
    if (unit === null && tiers.length) unit = tiers[0].price;

    const priced = qty > 0 && unit !== null;
    const subtotal = priced ? qty * unit : 0;

    this.#fill(this.fields.qty, qty > 0 ? String(qty) : '');
    this.#fill(this.fields.unit, priced ? this.#money(unit) : '');
    this.#fill(this.fields.total, priced ? this.#money(subtotal) : '');
  }

  #fill(el, value) {
    if (el) el.value = value;
  }
}

if (!customElements.get('spikey-quote')) {
  customElements.define('spikey-quote', SpikeyQuote);
}
